import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

// CTV-743: the outstanding-task prompt for a station with no connected payout
// destination. Deliberately a POST-onboarding prompt rather than an onboarding
// step: onboarding is one-time and every unpaid station today finished it long
// ago, so a step there would have caught none of them.
//
// Scoped to the station OVERVIEW only. It is a standing task, not an
// interruption, and repeating it on every tab reads as an error state rather
// than a to-do. The sidebar dot is what carries the unresolved state everywhere
// else, and it is always one click from the fix.
//
// No dismiss control: the consequence is that money cannot reach the publisher
// at all, and the fix depends on them acting.
//
// CTV-759: in All stations the same banner names EVERY station that needs
// setup, each linking to its own Payout method tab. Multi-station managers
// now land on /admin/all by default, so suppressing the nag there would
// silently stop nagging exactly the people who most need it.
interface PayoutSetupBannerProps {
  /** The scope's slug: a station slug, or `all`. Drives the overview test. */
  slug: string;
  /** True only when at least one station in scope is known NOT to be connected. */
  needsSetup: boolean;
  /** Current pathname; the banner renders on the overview only. */
  pathname: string;
  /**
   * Stations needing setup. One entry in single-station mode. Rendered as
   * per-station links when there is more than one.
   */
  unreadyStations?: Array<{ slug: string | null; displayName: string }>;
}

const PayoutSetupBanner: React.FC<PayoutSetupBannerProps> = ({
  slug,
  needsSetup,
  pathname,
  unreadyStations = [],
}) => {
  const overviewPath = `/admin/${slug}`;
  const onOverview = pathname === overviewPath || pathname === `${overviewPath}/`;
  if (!needsSetup || !onOverview) return null;
  // In All stations the CTA can't be a single button, so the station list
  // carries the links. In single-station mode it stays the one button.
  const linkable = unreadyStations.filter((s) => s.slug);
  const rollUp = slug === 'all' && linkable.length > 0;
  // CTV-787: the payout setup lives on the Payout method tab of Station
  // settings now.
  const paymentsPath = rollUp
    ? `/admin/${linkable[0].slug}/settings/payout-method`
    : `/admin/${slug}/settings/payout-method`;

  return (
    // Amber, not red: nothing is broken and no money is lost, the publisher
    // simply has an outstanding task. Red is reserved for genuine errors, and
    // spending it here would flatten the difference. The rail and uppercase
    // label keep it prominent without shouting.
    <div
      role="status"
      data-testid="payout-setup-banner"
      className="mb-6 rounded-xl border border-amber-200 bg-amber-50 border-l-[3px] border-l-amber-500 px-4 py-3"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-amber-700">
              {rollUp && linkable.length > 1
                ? `Payouts not set up · ${linkable.length} stations`
                : 'Payouts not set up'}
            </div>
            {/* Second sentence kept verbatim from the banner this replaces
                (CTV-518): it is the clearest statement we have that booking is
                NOT blocked, which is the first thing a publisher worries about
                when they see a payment warning. */}
            <div className="mt-1 text-sm text-amber-900">
              Advertisers can already book and pay for campaigns. CTVBuyer collects payment and
              holds your net revenue until a payout method is connected. Your net revenue from each
              month's delivery is paid 60 days after that month ends.
            </div>
            {rollUp && (
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                {linkable.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/admin/${s.slug}/settings/payout-method`}
                    className="text-sm font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950"
                  >
                    {s.displayName}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
        {!rollUp && (
          <Link
            to={paymentsPath}
            className="shrink-0 rounded-md bg-amber-900 px-3 py-2 text-sm font-medium text-white hover:bg-amber-950 transition-colors"
          >
            Set up payouts
          </Link>
        )}
      </div>
    </div>
  );
};

export default PayoutSetupBanner;
